import React, { useState } from 'react';
import ReactModal, { BaseModalProps } from 'react-overlays/Modal';
import styled, { css } from 'styled-components';
import { useModalWrapperCenteredCss, useModalWrapperCss } from '../Modal/Modal';
import { CloseButton } from '../../components/CloseButton/CloseButton';
import { useWindowDimensions } from '../../hooks/useResponsive/useResponsive';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { Overlay } from '../../components/Overlay/Overlay';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { BaseDiv } from '../../common/BaseElements';
import { LoadingAnimation } from '../../components/LoadingAnimation/LoadingAnimation';

export interface IVideoPlayerProps extends Omit<BaseModalProps, 'children'> {
  videoUrl: string;
  onClose?: () => void;
}

const VideoModal = styled(ReactModal)`
  ${useModalWrapperCss};
  ${useModalWrapperCenteredCss};
`;

const ModalCloseButton = styled(CloseButton)`
  position: absolute;
  top: 0;
  right: 0;
`;

const ModalContent = styled(BaseDiv)<{ isMobile: boolean }>`
  padding: ${({ isMobile }) => PX_TO_REM[isMobile ? 24 : 36]};
  position: relative;
  max-width: 100%;
  width: ${PX_TO_REM[1200]};
  height: auto;
  aspect-ratio: 16/9;
  display: flex;
`;

const VideoFrame = styled.iframe<{ loaded: boolean }>`
  height: 100%;
  width: 100%;
  border: none;
  ${({ loaded }) =>
    !loaded &&
    css`
      display: none;
    `};
`;

const SpinnerContainer = styled(BaseDiv)`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const VideoPlayer: React.FC<IVideoPlayerProps> = ({ videoUrl, onClose, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'VideoPlayer');
  const { isMobile } = useWindowDimensions();
  const [loaded, setLoaded] = useState(false);

  const handleOnClose = () => {
    (onClose ?? rest.onHide)?.();
    setLoaded(false);
  };

  return (
    <VideoModal renderBackdrop={(props: any) => <Overlay {...props} blur={false} />} {...generateTestId()} {...rest}>
      <ModalContent isMobile={isMobile}>
        {loaded && <ModalCloseButton onClick={handleOnClose} variant='white50' size={isMobile ? SIZES.XX_SMALL : SIZES.SMALL} />}
        {!loaded && (
          <SpinnerContainer>
            <LoadingAnimation variant='white' size='large' />
          </SpinnerContainer>
        )}
        <VideoFrame loaded={loaded} onLoad={() => setLoaded(true)} src={videoUrl} allowFullScreen {...generateTestId('iframe')} />
      </ModalContent>
    </VideoModal>
  );
};
